import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Send, Paperclip, Image as ImageIcon, Cake } from 'lucide-react';
import { PageHeader, StatusBadge, CategoryBadge, Empty, PageLoader, CATEGORY_LABELS, fmtDateTime, Spinner } from '../../components/ui';
import api, { errMsg } from '../../services/api';

export default function Notifications() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState({ status: '', category: '' });
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(Object.entries(filter).filter(([, v]) => v)).toString();
    api.get(`/admin/notifications?${q}`).then(r => setRows(r.data));
  }, [filter]);

  // Polls while anything is sending
  useEffect(() => {
    if (!rows?.some(r => r.status === 'SENDING')) return;
    const t = setTimeout(() => setFilter(f => ({ ...f })), 3000);
    return () => clearTimeout(t);
  }, [rows]);

  const runBirthdays = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/notifications/run-birthdays');
      const sent = data.reduce((a, g) => a + g.sent, 0);
      toast.success(sent ? `Queued ${sent} birthday email(s)` : 'No new birthdays to greet today');
      setFilter(f => ({ ...f }));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setRunning(false);
    }
  };

  if (!rows) return <PageLoader />;

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Announcements, events, projects and automatic birthday wishes"
        actions={<>
          <button onClick={runBirthdays} disabled={running} className="btn-outline" title="Birthday emails are sent automatically every morning. Use this to send today's now.">
            {running ? <Spinner size={16} /> : <Cake size={16} />}Send today's birthdays
          </button>
          <Link to="/admin/notifications/new" className="btn-gold"><Plus size={16} />New notification</Link>
        </>} />

      <div className="mb-4 flex flex-wrap gap-2">
        <select className="input w-auto py-1.5" value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option><option value="DRAFT">Drafts</option><option value="SENDING">Sending</option><option value="SENT">Sent</option><option value="FAILED">Failed</option>
        </select>
        <select className="input w-auto py-1.5" value={filter.category} onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}>
          <option value="">All types</option>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="card overflow-hidden">
        {!rows.length ? <Empty icon={Send} title="No notifications yet"><Link to="/admin/notifications/new" className="text-navy-700 underline">Write your first one</Link></Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="th">Subject</th><th className="th">Type</th><th className="th">Layout</th><th className="th">Date</th><th className="th">Delivered</th><th className="th">Opened</th><th className="th">Status</th></tr></thead>
              <tbody className="divide-y">
                {rows.map(n => (
                  <tr key={n.id} onClick={() => navigate(n.status === 'DRAFT' ? `/admin/notifications/${n.id}/edit` : `/admin/notifications/${n.id}`)} className="cursor-pointer hover:bg-navy-50/50">
                    <td className="td">
                      <p className="flex items-center gap-1.5 font-medium text-navy-900">{n.subject}{n.imagePath && <ImageIcon size={13} className="text-gray-400" />}{n.attachmentName && <Paperclip size={13} className="text-gray-400" />}</p>
                      {n.createdBy && <p className="text-xs text-gray-400">by {n.createdBy.name}</p>}
                    </td>
                    <td className="td"><CategoryBadge category={n.category} /></td>
                    <td className="td text-gray-600">{n.layout?.name || '—'}</td>
                    <td className="td whitespace-nowrap text-gray-500">{fmtDateTime(n.sentAt || n.createdAt)}</td>
                    <td className="td">{n.status === 'DRAFT' ? '—' : `${n.sentCount}/${n.totalCount}`}{n.failedCount > 0 && <span className="ml-1 text-xs text-red-600">({n.failedCount} failed)</span>}</td>
                    <td className="td">{n.sentCount ? `${n.openedCount} (${Math.round((n.openedCount / n.sentCount) * 100)}%)` : '—'}</td>
                    <td className="td"><StatusBadge status={n.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
